import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import {
  getShadowStyle,
  useWindowWidth,
} from '@awaze/design-system.utils.react'

import {
  getIsDesktop,
  getIsTablet,
  onMobile,
  onTablet,
} from '../utils/dimension'

const MobileStickyContainer = styled.div<{ stuck: boolean }>`
  ${onMobile} {
    ${(p) =>
      p.stuck &&
      css`
        position: fixed; /** Safari on iPhone does not handle sticky very well, so we set fixed manually when needed */
        left: 0;
        top: 0;
        width: 100%;
        z-index: 20; /** Ensure higher z-index than the header */
        background: #fff;
        box-shadow: ${({ theme }) => getShadowStyle({ theme, depth: 3 })};
      `}
  }
`

export const MobileSticky: React.FC<{
  children:
    | React.ReactNode
    | (({ stuck }: { stuck: boolean }) => React.ReactNode)
  isGuestApp?: boolean
}> = ({ children, isGuestApp }) => {
  const [stuck, setStuck] = useState(false)
  const positionRef = useRef<HTMLDivElement>(null)
  const heightRef = useRef<HTMLDivElement>(null)
  const width = useWindowWidth()
  useEffect(() => {
    const currentRef = positionRef.current
    if (!currentRef || !width || getIsTablet(width) || getIsDesktop(width)) {
      return
    }

    const handler = () => {
      if (isGuestApp) {
        return setStuck(document.documentElement.scrollTop > 0)
      }
      setStuck(currentRef.offsetTop < document.documentElement.scrollTop)
    }

    /**
     * Ideally we would not add a listener to the scroll event, but use and intersection-observer instead.
     * Unfortunately, that has very poor performance in safari on the iPhone
     */
    window.addEventListener('scroll', handler)
    return () => window.removeEventListener('scroll', handler)
  }, [stuck, width])
  return (
    <>
      <div
        ref={positionRef}
        style={{
          marginBottom: stuck ? heightRef.current?.offsetHeight : 0,
        }}
      />
      <MobileStickyContainer stuck={stuck} ref={heightRef}>
        {typeof children === 'function' ? children({ stuck }) : children}
      </MobileStickyContainer>
    </>
  )
}

export const MobileStickableContent = styled.div<{ stuck: boolean }>`
  ${onTablet} {
    display: none;
  }

  padding: 0 18px;

  margin-top: ${(p) => (p.stuck ? 0 : 8)}px;

  transition: 300ms margin ease-out;

  > {
    justify-content: space-around;
  }
`
